import { Button, Checkbox, NumberInput, TextInput, Tooltip } from "@mantine/core";
import { IconArrowRight, IconClipboardText } from "@tabler/icons-react";
import type { RfqLine, RfqQuote, RfqVendor } from "@/lib/rfq/types";
import { useMemo, useState } from "react";

import { Modal } from "antd";
import type { QuoteItemInput } from "@/lib/rfq/api";
import { fmtUnitPrice } from "@/lib/rfq/calc";
import { useRfqLabels } from "@/lib/rfq/labels";

interface RowState {
  quoteItemId?: string;
  supplied: boolean;
  unitPrice: string;
  freightIncluded: boolean;
  taxIncluded: boolean;
  deliveryDate: string;
  remark: string;
}

function RfqRecordQuoteModal({
  open,
  onClose,
  vendor,
  lines,
  quotes,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  vendor: RfqVendor | null;
  lines: RfqLine[];
  quotes: RfqQuote[];
  onSubmit: (vendorId: string, rows: QuoteItemInput[]) => Promise<void> | void;
}) {
  const { t, category } = useRfqLabels();
  const [saving, setSaving] = useState(false);

  const prevByLine = useMemo(() => {
    const map = new Map<string, RfqQuote>();
    if (!vendor) return map;
    for (const quote of quotes) {
      if (quote.vendorId !== vendor.id) continue;
      const current = map.get(quote.lineId);
      if (!current || quote.round > current.round) map.set(quote.lineId, quote);
    }
    return map;
  }, [vendor, quotes]);

  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [lastKey, setLastKey] = useState("");

  const initKey = `${vendor?.id ?? ""}-${open}`;
  if (open && vendor && initKey !== lastKey) {
    const next: Record<string, RowState> = {};
    for (const line of lines) {
      const prev = prevByLine.get(line.id);
      next[line.id] = {
        quoteItemId: prev?.id,
        supplied: prev ? prev.supplied : true,
        unitPrice: prev && prev.supplied ? String(prev.unitPrice) : "",
        freightIncluded: prev ? prev.freightIncluded : true,
        taxIncluded: prev ? prev.taxIncluded : false,
        deliveryDate: prev?.deliveryDate ?? "",
        remark: prev?.remark ?? "",
      };
    }
    setRows(next);
    setLastKey(initKey);
  }

  const isReQuote = Array.from(prevByLine.values()).some(
    (quote) => quote.supplied,
  );

  const patch = (lineId: string, value: Partial<RowState>) =>
    setRows((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...value } }));

  const handleSubmit = async () => {
    if (!vendor) return;
    setSaving(true);
    try {
      const payload: QuoteItemInput[] = lines.map((line) => {
        const row = rows[line.id];
        const price = Number(row?.unitPrice);
        const supplied = !!row?.supplied && row.unitPrice !== "" && price > 0;
        return {
          quoteItemId: row?.quoteItemId,
          rfqItemId: line.id,
          isAvailable: !!row?.supplied,
          unitPrice: supplied ? price : null,
          includesFreight: !!row?.freightIncluded,
          includesTax: !!row?.taxIncluded,
          deliveryDate: row?.deliveryDate || undefined,
          remark: row?.remark || undefined,
        };
      });
      await onSubmit(vendor.id, payload);
      setLastKey("");
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!vendor) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={860}
      footer={null}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      title={
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-base font-medium text-gray-700">
            <IconClipboardText size={18} className="text-blue-600" />
            {t("rfq.record.title", {
              defaultValue: "代錄報價 — {{vendor}}",
              vendor: vendor.name,
            })}
          </span>
          <span className="text-xs font-normal text-gray-500">
            {isReQuote
              ? t("rfq.record.descriptionReQuote") ||
                "廠商回覆議價，填入本次報價；系統會保留前次報價並計算降幅。"
              : t("rfq.record.descriptionNew") ||
                "以電話 / Email / 傳真回覆的報價，由承辦代為登錄。"}
          </span>
        </div>
      }
    >
      <div className="max-h-[60vh] overflow-y-auto border-t border-gray-200 p-4">
        <div className="flex flex-col gap-2">
          {lines.map((line) => {
            const row = rows[line.id];
            if (!row) return null;
            const prev = prevByLine.get(line.id);
            const newPrice = Number(row.unitPrice);
            const showDiff =
              !!prev &&
              prev.supplied &&
              row.supplied &&
              row.unitPrice !== "" &&
              prev.unitPrice > 0;
            const diffPct = showDiff
              ? ((newPrice - (prev as RfqQuote).unitPrice) /
                  (prev as RfqQuote).unitPrice) *
                100
              : null;
            return (
              <div
                key={line.id}
                className={`flex flex-col gap-2 rounded-lg border border-gray-200 p-3 ${
                  row.supplied ? "" : "bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2">
                    <span className="mt-0.5 text-xs font-medium text-gray-500">
                      {line.seq}
                    </span>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium text-gray-700">
                        {line.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {line.spec || "—"} ・ {category(line.category)} ・{" "}
                        {t("rfq.record.requirement") || "需求"} {line.qty}{" "}
                        {line.unit}
                      </span>
                    </div>
                  </div>
                  <Checkbox
                    size="xs"
                    checked={row.supplied}
                    label={t("rfq.record.supplied") || "可供應"}
                    onChange={(event) =>
                      patch(line.id, { supplied: event.currentTarget.checked })
                    }
                  />
                </div>

                {row.supplied ? (
                  <div className="flex flex-wrap items-end gap-3 pl-6">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">
                        {t("rfq.record.unitPrice") || "報價單價"}
                      </span>
                      <NumberInput
                        size="xs"
                        className="w-32"
                        hideControls
                        min={0}
                        value={row.unitPrice}
                        placeholder="0"
                        onChange={(value) =>
                          patch(line.id, { unitPrice: String(value ?? "") })
                        }
                        aria-label={`${line.name} ${t("rfq.record.unitPrice") || "報價單價"}`}
                      />
                    </div>
                    {/* <Tooltip
                      label={
                        t("rfq.record.quoteUnitLocked") ||
                        "後端以詢價單位計價，報價單位固定同詢價明細"
                      }
                      withArrow
                      multiline
                      w={240}
                    >
                      <div className="flex flex-col gap-1 opacity-70">
                        <span className="text-xs text-gray-500">
                          {t("rfq.record.quoteUnit") || "報價單位"}
                        </span>
                        <span className="flex h-[30px] w-24 items-center rounded border border-gray-200 bg-gray-50 px-2 text-sm text-gray-600">
                          {line.unit || "—"}
                        </span>
                      </div>
                    </Tooltip> */}
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">
                        {t("rfq.record.deliveryDate") || "承諾交期"}
                      </span>
                      <TextInput
                        size="xs"
                        type="date"
                        className="w-36"
                        value={row.deliveryDate}
                        onChange={(event) =>
                          patch(line.id, {
                            deliveryDate: event.currentTarget.value,
                          })
                        }
                        aria-label={`${line.name} ${t("rfq.record.deliveryDate") || "承諾交期"}`}
                      />
                    </div>
                    <Checkbox
                      size="xs"
                      className="pb-1.5"
                      checked={row.freightIncluded}
                      label={t("rfq.record.freightIncluded") || "含運至工地"}
                      onChange={(event) =>
                        patch(line.id, {
                          freightIncluded: event.currentTarget.checked,
                        })
                      }
                    />
                    <Checkbox
                      size="xs"
                      className="pb-1.5"
                      checked={row.taxIncluded}
                      label={t("rfq.record.taxIncluded") || "報價含稅"}
                      onChange={(event) =>
                        patch(line.id, {
                          taxIncluded: event.currentTarget.checked,
                        })
                      }
                    />
                    {showDiff && diffPct != null ? (
                      <span
                        className={`mb-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${
                          diffPct < 0
                            ? "bg-emerald-50 text-emerald-600"
                            : diffPct > 0
                              ? "bg-red-50 text-red-600"
                              : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        <span>
                          {fmtUnitPrice((prev as RfqQuote).unitPrice)}
                        </span>
                        <IconArrowRight size={12} />
                        <span>{fmtUnitPrice(newPrice)}</span>
                        {diffPct !== 0 ? (
                          <span>
                            {diffPct < 0 ? "↓" : "↑"}
                            {Math.abs(diffPct).toFixed(1)}%
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    <TextInput
                      size="xs"
                      className="w-full"
                      value={row.remark}
                      placeholder={t("rfq.record.remark") || "備註"}
                      onChange={(event) =>
                        patch(line.id, { remark: event.currentTarget.value })
                      }
                      aria-label={`${line.name} ${t("rfq.record.remark") || "備註"}`}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
        <Button variant="default" radius="md" size="sm" onClick={onClose}>
          {t("rfq.record.cancel") || "取消"}
        </Button>
        <Button
          radius="md"
          size="sm"
          color="blue"
          loading={saving}
          leftSection={<IconClipboardText size={14} />}
          onClick={handleSubmit}
        >
          {t("rfq.record.save") || "儲存報價"}
        </Button>
      </div>
    </Modal>
  );
}

export default RfqRecordQuoteModal;
